import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import { Lock, X, UserPlus, Loader2, AlertCircle, Users, Shield } from 'lucide-react';

/**
 * Modal for managing component confidentiality settings.
 * Allows users to toggle confidentiality and manage the access list.
 *
 * Props:
 * - isOpen: boolean - Whether the modal is open
 * - onClose: function - Callback when modal is closed
 * - target: object - Target component info:
 *   - isUnfiled: boolean - Whether this is an unfiled/inbox component
 *   - tripId: string - ID of the trip (for trip components)
 *   - componentId: string - ID of the component
 *   - title: string - Display title for the component
 * - onUpdate: function - Callback when settings are updated
 */
const ConfidentialitySettingsModal = ({
  isOpen,
  onClose,
  target,
  onUpdate
}) => {
  const [isConfidential, setIsConfidential] = useState(false);
  const [initialIsConfidential, setInitialIsConfidential] = useState(false);
  const [accessList, setAccessList] = useState([]);
  const [initialAccessList, setInitialAccessList] = useState([]);
  const [orgUsers, setOrgUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Load component data and organization users when modal opens
  useEffect(() => {
    if (isOpen && target) {
      loadComponentData();
      loadOrgUsers();
      setHasChanges(false);
      setError('');
    }
  }, [isOpen, target?.componentId, target?.tripId, target?.isUnfiled]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Track changes to confidentiality or access list
  useEffect(() => {
    const confidentialityChanged = isConfidential !== initialIsConfidential;
    const accessListChanged = JSON.stringify(accessList) !== JSON.stringify(initialAccessList);
    setHasChanges(confidentialityChanged || accessListChanged);
  }, [isConfidential, initialIsConfidential, accessList, initialAccessList]);

  const loadComponentData = async () => {
    if (!target) return;

    try {
      setLoading(true);
      const apiBase = getApiBase();
      const headers = getAuthHeaders();

      if (target.isUnfiled) {
        // Fetch unfiled component data
        const response = await axios.get(`${apiBase}/unfilled-components/${target.componentId}`, { headers });
        const component = response.data;
        const isConf = component.is_confidential_inbox || false;
        setIsConfidential(isConf);
        setInitialIsConfidential(isConf);
        // Get access list from unfiled component (may be set before assigning to trip)
        const currentAccessList = component.access_list || [];
        setAccessList(currentAccessList);
        setInitialAccessList(currentAccessList);
      } else if (target.tripId && target.componentId) {
        // Fetch trip data to get component confidentiality status
        const tripResponse = await axios.get(`${apiBase}/trips/${target.tripId}`, { headers });
        const trip = tripResponse.data;

        // Check component_permission_overrides for confidentiality
        const overrides = trip.component_permission_overrides || [];
        const override = overrides.find(o => o.component_id === target.componentId);

        // Also check the component's is_confidential field
        const component = (trip.components || []).find(c => c.id === target.componentId);
        const isConf = override?.is_confidential || component?.is_confidential || false;

        setIsConfidential(isConf);
        setInitialIsConfidential(isConf);

        // Get access list from the component
        const currentAccessList = component?.access_list || [];
        setAccessList(currentAccessList);
        setInitialAccessList(currentAccessList);
      }
    } catch (err) {
      console.error('Failed to load component data:', err);
      setError('Failed to load component data');
    } finally {
      setLoading(false);
    }
  };

  const loadOrgUsers = async () => {
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      const response = await axios.get(`${apiBase}/users`, { headers });
      setOrgUsers(response.data || []);
    } catch (err) {
      console.error('Failed to load users:', err);
      // Don't set error for this - it's not critical
    }
  };

  const handleConfidentialityToggle = () => {
    setIsConfidential(!isConfidential);
  };

  const handleAddUser = (user) => {
    const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
    const newEntry = {
      user_id: user.id,
      user_name: `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email,
      user_email: user.email,
      added_by_id: currentUser.id,
      added_by_name: `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim(),
      added_at: new Date().toISOString()
    };
    setAccessList([...accessList, newEntry]);
    setUserSearch('');
    setShowUserDropdown(false);
  };

  const handleRemoveUser = (userId) => {
    setAccessList(accessList.filter(entry => entry.user_id !== userId));
  };

  const handleSave = async () => {
    if (!target) return;

    try {
      setSaving(true);
      setError('');
      const apiBase = getApiBase();
      const headers = getAuthHeaders();

      if (target.isUnfiled) {
        // Update unfiled component confidentiality and access list
        await axios.patch(
          `${apiBase}/unfilled-components/${target.componentId}/confidentiality`,
          {
            is_confidential: isConfidential,
            access_list: accessList.map(entry => ({
              user_id: entry.user_id,
              user_name: entry.user_name,
              user_email: entry.user_email,
              added_by_id: entry.added_by_id,
              added_by_name: entry.added_by_name,
              added_at: entry.added_at
            }))
          },
          { headers }
        );
      } else if (target.tripId && target.componentId) {
        // Update trip component confidentiality
        if (isConfidential !== initialIsConfidential) {
          await axios.patch(
            `${apiBase}/trips/${target.tripId}/components/${target.componentId}/confidentiality`,
            { is_confidential: isConfidential },
            { headers }
          );
        }

        // Update access list if there are changes
        const accessListChanged = JSON.stringify(accessList) !== JSON.stringify(initialAccessList);
        if (accessListChanged) {
          await axios.put(
            `${apiBase}/trips/${target.tripId}/components/${target.componentId}`,
            {
              access_list: accessList.map(entry => ({
                user_id: entry.user_id,
                user_name: entry.user_name,
                user_email: entry.user_email,
                added_by_id: entry.added_by_id,
                added_by_name: entry.added_by_name,
                added_at: entry.added_at
              }))
            },
            { headers }
          );
        }
      }

      // Notify parent of update
      if (onUpdate) {
        onUpdate({ is_confidential: isConfidential, access_list: accessList });
      }

      onClose();
    } catch (err) {
      console.error('Failed to save confidentiality settings:', err);
      setError(err.response?.data?.detail || 'Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const filteredUsers = orgUsers.filter(user => {
    const searchLower = userSearch.toLowerCase();
    const fullName = `${user.first_name || ''} ${user.last_name || ''}`.toLowerCase();
    const email = (user.email || '').toLowerCase();
    const matchesSearch = fullName.includes(searchLower) || email.includes(searchLower);
    const notAlreadyAdded = !accessList.some(entry => entry.user_id === user.id);
    // Don't show current user
    const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
    const notCurrentUser = user.id !== currentUser.id;
    return matchesSearch && notAlreadyAdded && notCurrentUser;
  }).slice(0, 10);

  if (!isOpen || !target) return null;

  // Modal styles based on theme
  const modalBg = 'bg-ui-raised border-ui-line text-ui-ink';
  const headerBg = 'bg-ui-hover/50';
  const textPrimary = 'text-ui-ink';
  const textSecondary = 'text-ui-ink-3';
  const textMuted = 'text-ui-muted';
  const inputBg = 'bg-ui-sunk border-ui-control text-ui-ink placeholder-ui-faint';
  const dropdownBg = 'bg-ui-raised border-ui-line';
  const hoverBg = 'hover:bg-ui-hover';
  const cardBg = 'bg-ui-hover border-ui-line';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-ui-overlay/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className={`relative w-full max-w-md rounded-2xl border shadow-ui-pop ${modalBg}`}>
        {/* Header */}
        <div className={`flex items-center justify-between px-6 py-4 border-b border-ui-line ${headerBg} rounded-t-2xl`}>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-ui-amber-bg">
              <Shield className="w-5 h-5 text-ui-amber-fg" />
            </div>
            <div>
              <h2 className={`text-lg font-semibold ${textPrimary}`}>Confidentiality Settings</h2>
              <p className={`text-sm ${textSecondary} truncate max-w-[200px]`} title={target.title}>
                {target.title || 'Component'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg transition-colors hover:bg-ui-hover text-ui-muted"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-5 space-y-5">
          {error && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-ui-red-bg text-ui-red-fg">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-ui-muted" />
            </div>
          ) : (
            <>
              {/* Confidentiality Toggle */}
              <div className={`p-4 rounded-xl border ${cardBg}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Lock className={`w-5 h-5 ${isConfidential ? 'text-ui-amber-fg' : textMuted}`} />
                    <div>
                      <div className={`font-medium ${textPrimary}`}>Confidential</div>
                      <div className={`text-sm ${textSecondary}`}>
                        {isConfidential
                          ? 'Only selected users can see this'
                          : 'Visible to all trip collaborators'}
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleConfidentialityToggle}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-ui-amber-solid focus:ring-offset-2 focus:ring-offset-ui-raised ${
                      isConfidential
                        ? 'bg-ui-amber-solid'
                        : 'bg-ui-faint'
                    }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-ui-on-primary shadow ring-0 transition duration-200 ease-in-out ${
                        isConfidential ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
              </div>

              {/* Access List Section - Show when confidential */}
              {isConfidential && (
                <div className="space-y-4">
                  <div className="p-3 rounded-lg bg-ui-amber-bg border border-ui-amber-line">
                    <p className="text-sm text-ui-amber-fg">
                      Only you, users in the access list below, supervisors, and org admins can see this item.
                    </p>
                  </div>

                  {/* Add User Section */}
                  <div>
                    <label className={`text-sm font-medium ${textPrimary} mb-2 block`}>
                      <Users className="w-4 h-4 inline mr-1.5" />
                      Users with Access
                    </label>

                    {/* Search Input */}
                    <div className="relative" ref={dropdownRef}>
                      <div className="relative">
                        <UserPlus className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${textMuted}`} />
                        <input
                          ref={searchInputRef}
                          type="text"
                          value={userSearch}
                          onChange={(e) => {
                            setUserSearch(e.target.value);
                            setShowUserDropdown(true);
                          }}
                          onFocus={() => setShowUserDropdown(true)}
                          placeholder="Search users to add..."
                          className={`w-full pl-10 pr-4 py-2.5 rounded-lg border text-sm transition-colors focus:ring-2 focus:ring-ui-amber-solid focus:border-ui-amber-solid ${inputBg}`}
                        />
                      </div>

                      {/* User Dropdown */}
                      {showUserDropdown && userSearch.trim() && (
                        <div className={`absolute z-10 w-full mt-1 rounded-lg border shadow-ui-pop max-h-48 overflow-y-auto ${dropdownBg}`}>
                          {filteredUsers.length > 0 ? (
                            filteredUsers.map(user => (
                              <button
                                key={user.id}
                                type="button"
                                onClick={() => handleAddUser(user)}
                                className={`w-full text-left px-4 py-2.5 ${hoverBg} transition-colors`}
                              >
                                <div className={`font-medium text-sm ${textPrimary}`}>
                                  {user.first_name} {user.last_name}
                                </div>
                                <div className={`text-xs ${textMuted}`}>{user.email}</div>
                              </button>
                            ))
                          ) : (
                            <div className={`px-4 py-3 text-sm ${textMuted}`}>
                              No users found
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Current Access List */}
                  <div className="space-y-2">
                    {accessList.length === 0 ? (
                      <div className={`text-sm ${textMuted} italic py-3 text-center`}>
                        No additional users added. Only you can see this item.
                      </div>
                    ) : (
                      accessList.map((entry) => (
                        <div
                          key={entry.user_id}
                          className={`flex items-center justify-between p-3 rounded-lg border ${cardBg}`}
                        >
                          <div className="min-w-0 flex-1">
                            <div className={`font-medium text-sm truncate ${textPrimary}`}>
                              {entry.user_name}
                            </div>
                            <div className={`text-xs truncate ${textMuted}`}>
                              {entry.user_email}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveUser(entry.user_id)}
                            className="ml-3 p-1.5 rounded-lg transition-colors text-ui-red-fg hover:bg-ui-red-bg"
                            title="Remove access"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}

            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-ui-line">
          <button
            onClick={onClose}
            disabled={saving}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors text-ui-ink-3 hover:bg-ui-hover ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || loading || !hasChanges}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 bg-ui-amber-strong text-ui-on-primary hover:bg-ui-amber-strong/90 ${(saving || loading || !hasChanges) ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfidentialitySettingsModal;
